import { useState, useEffect, useCallback, useRef } from "react";
import { InfoClient } from "../sdk";
import { toNumberFromSatoshi } from "../sdk/utils/math";
import { useSubscription } from "../context/SubscriptionContext";
import {
  RawBucket,
  BucketsUpdateMessage,
  MultiFeedBucketsData,
  MultiFeedBucketData,
} from "../sdk/types/api";
import {
  BucketCollectionsConfig,
  Bucket,
  transformRawBucketToBucket,
  convertMultiFeedToRawBucket,
  isRawBucket,
} from "../sdk/types/data";

/**
 * Hook for managing bucket collections data
 * Fetches buckets from API and calculates collection metrics
 */
export function useBucketCollections(config: BucketCollectionsConfig): {
  collections: Bucket[];
  loading: boolean;
  error: string;
  refreshBucketBalance: () => void;
  refreshSpotWalletBalance: () => Promise<void>;
  spotWalletBalance: number;
  isConnected: boolean;
} {
  const useWebSocket = config.useWebSocket !== false; // Default: true
  const [collections, setCollections] = useState<Bucket[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [spotWalletBalance, setSpotWalletBalance] = useState<number>(0);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const infoClientRef = useRef<InfoClient | null>(null);
  const refreshIntervalRef = useRef<number>(0);
  const isInitialLoadRef = useRef<boolean>(true);
  const configRef = useRef(config);
  configRef.current = config;
  const { subscribe, unsubscribe, isConnected: contextConnected } = useSubscription();

  /**
   * Transform bucket data from WebSocket to Bucket format
   * Handles both RawBucket and MultiFeedBucketData types from WebSocket
   * HTTP responses always use transformRawBucketToBucket directly
   */
  const transformBucket = useCallback(
    (bucket: RawBucket | MultiFeedBucketData): Bucket => {
      // Convert MultiFeedBucketData to RawBucket if needed
      const rawBucket: RawBucket = isRawBucket(bucket)
        ? bucket
        : convertMultiFeedToRawBucket(bucket);

      // Transform raw API data to Bucket type (handles satoshi conversion)
      return transformRawBucketToBucket(rawBucket);
    },
    [],
  );

  /**
   * Fetch spot wallet balance for USDC from API
   */
  const fetchSpotWalletBalance = useCallback(async () => {
    if (!infoClientRef.current) return;

    const currentConfig = configRef.current;
    if (
      !currentConfig.walletAddress ||
      currentConfig.walletAddress.trim() === ""
    ) {
      setSpotWalletBalance(0);
      return;
    }

    try {
      // Get USDC balance using getBalanceDecimal - converts satoshi strings to decimal numbers
      const balance = await infoClientRef.current.getBalanceDecimal(
        currentConfig.walletAddress,
        "USDC", // Specify USDC asset
      );
      // getBalanceDecimal converts available_balance to available as a number
      // Use available field (already converted to decimal), with fallback for edge cases
      const available = typeof balance.available === 'number' 
        ? balance.available
        : (balance.availableBalance ? toNumberFromSatoshi(balance.availableBalance) : 0);
      setSpotWalletBalance(available);
    } catch (err) {
      console.warn("Failed to fetch spot wallet balance (USDC):", err);
      setSpotWalletBalance(0);
    }
  }, []);

  /**
   * Fetch bucket collections from API
   * Uses configRef to access latest config without causing dependency loops
   */
  const fetchCollections = useCallback(async () => {
    if (!infoClientRef.current) return;
    const currentConfig = configRef.current;
    // Validate walletAddress (required field, but check for runtime safety)
    if (
      !currentConfig.walletAddress ||
      currentConfig.walletAddress.trim() === ""
    ) {
      // Clear error and collections when address is not available
      setError("Wallet address is required");
      setCollections([]);
      setLoading(false);
      isInitialLoadRef.current = false;
      return;
    }
    setError("");
    // Only show loading state on initial load, not on refreshes
    if (isInitialLoadRef.current) {
      setLoading(true);
    }
    try {
      console.log(
        "[useBucketCollections] [fetchCollections] Starting fetch...",
        {
          walletAddress: currentConfig.walletAddress,
          useWebSocket,
        },
      );

      // Fetch spot wallet balance in parallel
      fetchSpotWalletBalance();

      // Fetch buckets from API
      console.log(
        "[useBucketCollections] [fetchCollections] Calling getBuckets API...",
      );
      const response = await infoClientRef.current.getBuckets(
        currentConfig.walletAddress,
      );
      console.log(
        "[useBucketCollections] [fetchCollections] Raw API response:",
        {
          response,
          responseType: typeof response,
          isArray: Array.isArray(response),
          responseLength: Array.isArray(response) ? response.length : "N/A",
        },
      );

      const bucketsData = Array.isArray(response) ? response : [];
      console.log(
        "[useBucketCollections] [fetchCollections] Buckets data after normalization:",
        {
          bucketsData,
          bucketsDataLength: bucketsData.length,
          bucketsDataTypes: bucketsData.map((b, i) => ({
            index: i,
            type: typeof b,
            hasBucketId: !!(b?.bucketId),
            bucketId: (b as RawBucket).bucketId ?? "",
          })),
        },
      );

      // Transform buckets to collections
      console.log(
        `[useBucketCollections] [fetchCollections] About to transform ${bucketsData.length} buckets:`,
        bucketsData,
      );

      // HTTP API always returns RawBucket[], so use transformRawBucketToBucket directly
      const transformedCollections: Bucket[] = bucketsData.map(
        transformRawBucketToBucket,
      );

      setCollections(transformedCollections);

      // Only mark initial load as complete if WebSocket is not enabled
      // If WebSocket is enabled, handleBucketsUpdate will set loading to false when first WS data arrives
      if (!useWebSocket) {
        isInitialLoadRef.current = false;
        setLoading(false);
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Failed to load bucket collections";
      setError(errorMessage);
      console.error(
        "[useBucketCollections] [fetchCollections] Failed to fetch bucket collections:",
        {
          error: err,
          errorMessage,
          walletAddress: currentConfig.walletAddress,
          stack: err instanceof Error ? err.stack : undefined,
        },
      );

      // If WebSocket is not enabled or fetch failed, mark initial load as complete
      if (!useWebSocket) {
        isInitialLoadRef.current = false;
        setLoading(false);
      }
    }
  }, [fetchSpotWalletBalance, useWebSocket]); // transformRawBucketToBucket is a pure function, no dependencies needed

  /**
   * Handle bucket updates from WebSocket (multi-feed buckets sub-channel)
   * Backend sends MultiFeedBucketsData with buckets array for both initial snapshots and updates
   * Only updates state if data has actually changed to prevent unnecessary re-renders
   * Accepts unknown to match SubscriptionCallback; narrows to buckets payload inside.
   */
  const handleBucketsUpdate = useCallback(
    (data: unknown) => {
      const payload =
        data as MultiFeedBucketsData | BucketsUpdateMessage | null | undefined;
      if (!payload || !payload.buckets || !Array.isArray(payload.buckets)) {
        return;
      }
      console.log(
        "[useBucketCollections] [handleBucketsUpdate] WebSocket data received:",
        {
          data: payload,
          hasBuckets: !!payload.buckets,
          bucketsIsArray: Array.isArray(payload.buckets),
          bucketsLength: payload.buckets.length,
        },
      );
      // WebSocket format: { buckets: [...], total_buckets: number, address: string, timestamp: number }
      console.log(
        "[useBucketCollections] [handleBucketsUpdate] Transforming WebSocket buckets:",
        payload.buckets,
      );
      const transformedCollections: Bucket[] =
        payload.buckets.map(transformBucket);
      console.log(
        "[useBucketCollections] [handleBucketsUpdate] Transformed collections:",
        transformedCollections,
      );

      // Filter out any collections with empty bucketId or status is "dormant" (same as HTTP fetch)
      const validCollections = transformedCollections.filter(
        (c) =>
          c.bucketId && c.bucketId.trim() !== "" && c.status !== "dormant",
      );

      if (validCollections.length !== transformedCollections.length) {
        const filteredCount =
          transformedCollections.length - validCollections.length;
        const filteredByBucketId = transformedCollections.filter(
          (c) => !c.bucketId || c.bucketId.trim() === "",
        );
        const filteredByStatus = transformedCollections.filter(
          (c) =>
            c.bucketId && c.bucketId.trim() !== "" && c.status === "dormant",
        );
        console.warn(
          `[useBucketCollections] [handleBucketsUpdate] WebSocket: Filtered out ${filteredCount} collections (${filteredByBucketId.length} with empty bucketId, ${filteredByStatus.length} with status "dormant")`,
          {
            filteredByBucketId,
            filteredByStatus,
          },
        );
      }

      console.log(
        "[useBucketCollections] [handleBucketsUpdate] Setting collections from WebSocket:",
        {
          validCollectionsCount: validCollections.length,
          validCollections,
        },
      );

      // Compare with current collections to avoid unnecessary state updates
      setCollections((prev) => {
        console.log(
          "[useBucketCollections] [handleBucketsUpdate] Previous collections:",
          {
            prevCount: prev.length,
            prev,
          },
        );
        if (prev.length !== validCollections.length) {
          return validCollections;
        }
        // Deep comparison - only update if collections actually changed
        const prevStr = JSON.stringify(prev);
        const newStr = JSON.stringify(validCollections);
        return prevStr === newStr ? prev : validCollections;
      });
      // Mark initial load as complete when we receive websocket data
      if (isInitialLoadRef.current) {
        isInitialLoadRef.current = false;
        setLoading(false);
      }
    },
    [transformBucket],
  );

  // Initialize client and fetch collections
  useEffect(() => {
    // Validate required config fields (runtime safety check)
    if (!config.walletAddress || config.walletAddress.trim() === "") {
      setLoading(false);
      setError("Wallet address is required");
      setCollections([]);
      if (refreshIntervalRef.current !== 0) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = 0;
      }
      return;
    }

    const infoClient = new InfoClient({
      baseURL: config.baseURL,
      sessionToken: config.authToken,
    });
    infoClientRef.current = infoClient;
    isInitialLoadRef.current = true;
    fetchCollections();
    setIsConnected(contextConnected);

    const callback = (data: unknown) => handleBucketsUpdate(data);

    if (useWebSocket) {
      subscribe("buckets", callback);
      if (!contextConnected && config.refreshInterval && config.refreshInterval > 0) {
        refreshIntervalRef.current = window.setInterval(
          () => fetchCollections(),
          config.refreshInterval,
        );
      }
      return () => {
        if (refreshIntervalRef.current !== 0) {
          clearInterval(refreshIntervalRef.current);
          refreshIntervalRef.current = 0;
        }
        unsubscribe("buckets", callback);
      };
    }

    if (config.refreshInterval && config.refreshInterval > 0) {
      refreshIntervalRef.current = window.setInterval(
        () => fetchCollections(),
        config.refreshInterval,
      );
    }
    return () => {
      if (refreshIntervalRef.current !== 0) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = 0;
      }
    };
  }, [
    config.baseURL,
    config.authToken,
    config.walletAddress,
    config.refreshInterval,
    config.marketId,
    config.symbol,
    useWebSocket,
    contextConnected,
    subscribe,
    unsubscribe,
    fetchCollections,
    handleBucketsUpdate,
  ]);

  // Log what we're returning to the component
  useEffect(() => {
    console.log(
      "[useBucketCollections] [useEffect] Hook state changed, returning:",
      {
        collectionsCount: collections.length,
        collections,
        loading,
        error,
        spotWalletBalance,
      },
    );
  }, [collections, loading, error, spotWalletBalance]);

  return {
    collections,
    loading,
    error,
    refreshBucketBalance: fetchCollections,
    refreshSpotWalletBalance: fetchSpotWalletBalance,
    spotWalletBalance,
    isConnected,
  };
}
